// Quick-link count tile for the v2 dashboard. A label + a count; when `to` is set the whole
// tile links into the relevant list (with a chevron affordance) — a count shown with "›" means
// "open the filtered list".
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import SvgChevronRight from '@icons/chevron-right.svg?react';
import { formatNumber } from '@utils/utils.tsx';
import DashboardInfoTooltip from './DashboardInfoTooltip.tsx';

interface DashboardCountTileProps {
  // Static category shown as the first (top) line, e.g. "My shipments" / "Reservations".
  category?: string;
  // Specific task shown as the second line.
  label: string;
  count?: number;
  // Target list URL (already built via useNavigation). When set, the tile is a link.
  to?: string;
  // Optional info tooltip beside the label (icon shown only when provided).
  tooltip?: ReactNode;
}

const DashboardCountTile: React.FC<DashboardCountTileProps> = ({ category, label, count, to, tooltip }) => {
  const body = (
    <div
      className={twMerge(
        'flex items-center justify-between gap-3 px-4 py-3 bg-white border border-solid border-content-quaternary rounded-lg h-full',
        to && 'cursor-pointer transition-colors hover:border-content-brand',
      )}>
      <span className="flex flex-col leading-tight">
        {category && <span className="text-xs text-content-tertiary">{category}</span>}
        <span className="inline-flex items-center gap-1 text-sm text-content-primary">
          {label}
          <DashboardInfoTooltip content={tooltip} />
        </span>
      </span>
      <span className="inline-flex items-center gap-1 shrink-0">
        <span className="text-lg font-semibold text-content-brand">{count != null ? formatNumber(count, 0) : '—'}</span>
        {to && <SvgChevronRight className="w-3 h-3 text-content-brand" />}
      </span>
    </div>
  );

  return to ? (
    <Link to={to} className="block h-full">
      {body}
    </Link>
  ) : (
    body
  );
};

export default DashboardCountTile;